import { useState } from 'react';
import type { MutableRefObject } from 'react';
import type {
  GridApiCommon,
  GridEventListener,
  MuiEvent,
} from '@mui/x-data-grid';
import type { GridApiPro } from '@mui/x-data-grid-pro';

const RUN_FIRST = { isFirst: true };

function keepPage(_model: unknown, event: MuiEvent): void {
  event.defaultMuiPrevented = true;
}

const keepPageOnSort: GridEventListener<'sortModelChange'> = keepPage;
const keepPageOnFilter: GridEventListener<'filterModelChange'> = keepPage;

function subscribe(api: GridApiCommon): () => void {
  const offSort = api.subscribeEvent(
    'sortModelChange',
    keepPageOnSort,
    RUN_FIRST
  );
  const offFilter = api.subscribeEvent(
    'filterModelChange',
    keepPageOnFilter,
    RUN_FIRST
  );
  return () => {
    offSort();
    offFilter();
  };
}

function createKeepingPageRef<
  Api extends GridApiCommon
>(): MutableRefObject<Api | null> {
  let api: Api | null = null;
  let unsubscribe = (): void => undefined;
  return {
    get current(): Api | null {
      return api;
    },
    set current(next: Api | null) {
      if (next === api) return;
      unsubscribe();
      api = next;
      unsubscribe = next ? subscribe(next) : () => undefined;
    },
  };
}

/**
 * An `apiRef` for a grid that keeps its page and scroll position when the
 * sort or filter model changes, as MUI X 7 did by default. MUI X 8 always
 * returns to the first page and has no prop to stop it, but its pagination
 * hook skips an event whose `defaultMuiPrevented` is set, and these listeners
 * run first.
 *
 * Every `useGridEvent` subscriber to `sortModelChange` and `filterModelChange`
 * skips the marked event, not only pagination. That includes the `dataSource`
 * refetch and server-side lazy loading, so a grid using either must not use
 * this ref. `onSortModelChange` and `onFilterModelChange` still fire.
 *
 * The grid writes `current` when it mounts and clears it when it unmounts, so
 * the listeners follow the grid however late it mounts.
 */
export function useGridApiRefKeepingPage<
  Api extends GridApiCommon = GridApiPro
>(): MutableRefObject<Api | null> {
  const [apiRef] = useState(() => createKeepingPageRef<Api>());
  return apiRef;
}
